/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, { Component } from "react";
import styles from "./tabs.module.scss";
import PropTypes from "prop-types";
import Button from "../Button";
import Icon from "../Icon";

class TabButtons extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentTab: "",
      isOverflow: "",
      positionStyle: 0,
      buttonPositions: {
        left: "5px",
        right: "5px",
      },
    };
    this.tabItems = [];
  }

  createRefArray = () => {
    const numOfItems = this.props.buttons.length + 1;
    let i = 0;
    while (i < numOfItems) {
      this.tabItems[i] = React.createRef();
      i++;
    }
  };

  nextTab = () => {
    let { activeTab, buttons, changeTab } = this.props;
    buttons.map((button, index) => {
      return button === activeTab && changeTab(buttons[index + 1]);
    });
  };

  prevTab = () => {
    let { activeTab, buttons, changeTab } = this.props;
    buttons.map((button, index) => {
      return button === activeTab && changeTab(buttons[index - 1]);
    });
  };

  handleShow = (currentIndex) => {
    const {scrollToTop} = this.props;
    if (this.tabItems && this.tabItems[currentIndex] && this.tabItems[currentIndex].scrollIntoView) {
      if(scrollToTop) {
        this.tabItems[currentIndex].scrollIntoView({
          block: "end",
          inline: "center",
          behavior: "smooth",
        });
      }
    }
  };

  getActiveTabIndex = () => {
    let { activeTab, buttons } = this.props;
    let activeTabIndex = "";
    buttons.map((button, index) => {
      if (button === activeTab) {
        activeTabIndex = index;
      }
      return activeTabIndex;
    });
    this.setState(
      {
        currentTab: activeTabIndex,
      },
      () => {
        this.handleShow(this.state.currentTab);
      }
    );
  };

  focus = () => {
    this.tabItems[this.state.currentTab].children[0].children[0].focus();
  };

  isOverflown = () => {
    let buttonContainer = document.querySelector(
      `#${this.props.id}-button-container`
    );

    if (buttonContainer) {
      this.setState({
        isOverflow: buttonContainer.scrollWidth > buttonContainer.clientWidth,
      });
    }
  };

  detectHorizontalScrolling = () => {
    let buttonContainer = document.querySelector(
      `#${this.props.id}-button-container`
    );
    let scrolledPixels = buttonContainer.scrollLeft.toFixed();
    this.setState({ positionStyle: scrolledPixels }, () => {
      this.updatePositionStyles(this.state.positionStyle);
    });
  };

  updatePositionStyles = (pixels) => {
    let leftButtonAdjust = parseInt(pixels) + 5 + "px";
    let rightButtonAdjust =
      pixels >= 4 ? "-" + (parseInt(pixels) - 5) + "px" : "5px";
    this.setState((prevState) => ({
      buttonPositions: {
        ...prevState.buttonPositions,
        left: leftButtonAdjust,
        right: rightButtonAdjust,
      },
    }));
  };

  handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowRight":
        if (
          this.props.activeTab !==
          this.props.buttons[this.props.buttons.length - 1]
        ) {
          this.nextTab();
        } else {
          this.props.changeTab(this.props.buttons[0]);
        }
        e.preventDefault();
        break;
      case "ArrowLeft":
        if (this.props.activeTab !== this.props.buttons[0]) {
          this.prevTab();
        } else {
          this.props.changeTab(
            this.props.buttons[this.props.buttons.length - 1]
          );
        }
        e.preventDefault();
        break;
    }
  };

  componentDidMount() {
    this.createRefArray();
    this.getActiveTabIndex();
    this.isOverflown();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeTab !== this.props.activeTab) {
      this.getActiveTabIndex();
      setTimeout(() => this.focus(), 10);
    }
    window.addEventListener("resize", this.isOverflown);
  }

  render() {
    let { activeTab, buttons, changeTab, tabBackground } = this.props;
    const myStyles = {
      background: `linear-gradient(to right, transparent, ${tabBackground} )`,
      borderRadius: "4px",
    };
    return (
      <div
        id={`${this.props.id}-button-container`}
        style={{ backgroundColor: tabBackground }}
        className={styles.tab_buttons}
        onScroll={this.detectHorizontalScrolling}
      >
        {activeTab !== buttons[0] && this.state.isOverflow && this.props.navigationArrows && (
          <Button
            style={{
              ...myStyles,
              transform: `rotate(${"180deg"})`,
              left: this.state.buttonPositions.left,
            }}
            className={styles.tab_buttons_btn}
            varient="icon"
            disabled={false}
            onClick={() => this.prevTab()}
            aria-label={"previous"}
          >
            <span>
              <Icon
                fileName="chevron-next-sm-18"
                name="close-dialog"
                title="Close Dialog"
                focusable={false}
              />
            </span>
          </Button>
        )}

        {buttons.map((button, index) => {
          return (
            <div
              key={index}
              ref={(node) => (this.tabItems[index] = node)}
              className={styles.tab_button}
              onClick={() => changeTab(button)}
              onKeyDown={this.handleKeyDown}
              aria-live="polite"
              aria-label={`${button === activeTab ? `${button} selected` : ""}`}
            >
              <span
                className={`${styles.tab_underlineArea} ${
                  button === activeTab ? styles.tab_underlineArea_active : ""
                }`}
              >
                <span
                  tabIndex={`${button === activeTab ? 0 : -1}`}
                  className={styles.tab_focusArea}
                >
                  {button}
                </span>
              </span>
            </div>
          );
        })}
        {activeTab !== buttons[buttons.length - 1] && this.state.isOverflow && this.props.navigationArrows &&(
          <Button
            style={{ ...myStyles, right: this.state.buttonPositions.right }}
            className={styles.tab_buttons_btn}
            varient="icon"
            disabled={false}
            onClick={() => this.nextTab()}
            aria-label={"next"}
          >
            <span>
              <Icon
                fileName="chevron-next-sm-18"
                name="close-dialog"
                title="Close Dialog"
                focusable={false}
              />
            </span>
          </Button>
        )}
      </div>
    );
  }
}

TabButtons.propTypes = {
  activeTab: PropTypes.string,
  changeTab: PropTypes.func,
  id: PropTypes.string,
  navigationArrows: PropTypes.bool,
};

export default TabButtons;
